import React, { useState, useEffect, useRef } from "react";

// ----- Enhanced Typewriter Component (reuse from ServicesSection) -----
const Typewriter = ({ text, speed = 50, delay = 0, className = "", start = false }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [cursorVisible, setCursorVisible] = useState(true);
  const containerRef = useRef(null);
  const hiddenRef = useRef(null);
  const [minHeight, setMinHeight] = useState(0);

  // Blink the cursor.
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 530);
    return () => clearInterval(blinkInterval);
  }, []);

  // Measure the final text height once the hidden element is rendered.
  useEffect(() => {
    if (hiddenRef.current) {
      setMinHeight(hiddenRef.current.offsetHeight);
    }
  }, [text]);

  // Animate the text when 'start' is true.
  useEffect(() => {
    if (!start) {
      setDisplayedText("");
      return;
    }
    let animationFrame;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime - delay;
      if (elapsed < 0) {
        animationFrame = requestAnimationFrame(animate);
        return;
      }
      const totalDuration = text.length * speed;
      if (elapsed >= totalDuration) {
        setDisplayedText(text);
        return;
      }
      const targetIndex = Math.floor(elapsed / speed);
      setDisplayedText(text.slice(0, targetIndex + 1));
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [text, speed, delay, start]);

  return (
    <span
      ref={containerRef}
      className={className}
      style={{ minHeight: minHeight || "auto", position: "relative" }}
    >
      {displayedText}
      <span
        className="inline-block w-[2px] h-[1em] ml-1 -mb-[0.2em] bg-current"
        style={{ opacity: cursorVisible ? 1 : 0 }}
      />
      {/* Hidden element that renders the final text to reserve space */}
      <span
        ref={hiddenRef}
        style={{
          visibility: "hidden",
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          whiteSpace: "nowrap", // if your text is not multiline
        }}
      >
        {text}
      </span>
    </span>
  );
};

const projects = [
  {
    title: "AI-Driven E-commerce",
    description:
      "Implemented an AI-powered recommendation engine for a boutique fashion retailer, boosting sales by 80%.",
    result: "Significant increase in conversion rates & brand loyalty.",
  },
  {
    title: "Predictive Analytics in Healthcare",
    description:
      "Deployed an AI chatbot for a healthcare provider, reducing response times by 80% and enhancing patient engagement by 50%.",
    result: "Greater patient satisfaction & optimized scheduling.",
  },
  {
    title: "NLP-Enhanced Marketing Campaign",
    description:
      "Leveraged Natural Language Processing to create personalized email campaigns, improving open rates by 40%.",
    result: "Targeted outreach & increased ROI from email marketing.",
  },
  {
    title: "Automated Social Media Management",
    description:
      "Developed an AI scheduling and sentiment analysis tool for social channels, leading to a 60% surge in engagement.",
    result: "Unified brand voice & higher audience interaction.",
  },
];

const ProjectsSection = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  // Trigger typewriter animation when the section is visible.
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" ref={sectionRef} className="relative py-20 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-mono font-bold mb-12 text-center">
          <Typewriter text="PROJECTS.MATRIX" speed={60} start={isInView} className="inline-block" />
          <span className="block text-sm text-green-500/60 mt-2">
            <Typewriter text="// Showcasing Solutions" speed={60} delay={800} start={isInView} className="inline-block" />
          </span>
        </h2>

        <div className="grid gap-8 md:grid-cols-2">
          {projects.map((proj, i) => (
            <div key={i} className="border border-green-500/30 p-6 bg-gray-900/80 relative">
              <h3 className="text-xl font-mono font-bold mb-3 text-green-400">
                <Typewriter text={proj.title} speed={40} delay={i * 300} start={isInView} className="inline-block" />
              </h3>
              <p className="text-green-300/80 mb-4">
                <Typewriter text={proj.description} speed={30} delay={i * 300 + 500} start={isInView} className="inline-block" />
              </p>
              <p className="text-green-500/80 font-mono text-sm">
                <span className="font-bold">Result: </span>
                <Typewriter text={proj.result} speed={30} delay={i * 300 + 800} start={isInView} className="inline-block" />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
